<script>
import {
    BBadge, BCard, BCardTitle, BTab, BTabs,
} from 'bootstrap-vue'
import BusinessRule from '@/views/administration/legislation/components/BusinessRule.vue'
import BusinessRuleExplanation from '@/views/administration/legislation/components/BusinessRuleExplanation.vue'
import Button from '@core/components/common/Button.vue'
import { i18nApi, LegislationApi } from '@core/api'
import ExplanationModal from '@core/components/common/expression-language/ExplanationModal.vue'

const explanationRuleTypes = [
    'incomplete_explanation',
    'not_applicable_explanation',
    'applicable_explanation',
    'exempted_explanation',
]

export default {
    components: {
        ExplanationModal,
        BusinessRuleExplanation,
        Button,
        BBadge,
        BCard,
        BCardTitle,
        BTab,
        BTabs,
        BusinessRule,
    },
    props: {
        legislation: {
            type: Object,
            default() {
                return {
                    expressions: [],
                }
            },
        },
    },
    data() {
        return {
            expressionLanguageExplanation: null,
            languages: [],
            businessRuleGroups: [
                {
                    type: 'allow_applicable',
                    titleKey: 'Component(LegislationBusinessRules):applicability-allowance',
                },
                {
                    type: 'applicable',
                    titleKey: 'Component(LegislationBusinessRules):applicability',
                },
                {
                    type: 'eop_applicable_check',
                    titleKey: 'Component(LegislationBusinessRules):eop_when_applicable_check',
                },
                {
                    type: 'exemption',
                    titleKey: 'Component(LegislationBusinessRules):exemptions',
                },
            ],
            explanationRuleGroups: [
                {
                    type: 'incomplete_explanation',
                    title: 'Incomplete explanation',
                },
                {
                    type: 'not_applicable_explanation',
                    title: 'Not applicable explanation',
                },
                {
                    type: 'applicable_explanation',
                    title: 'Applicable explanation',
                },
                {
                    type: 'exempted_explanation',
                    title: 'Exempted explanation',
                },
            ],
        }
    },
    watch: {
        legislation() {
            this.ensureExplanationRuleTranslations()
        },
    },
    created() {
        LegislationApi.getExpressionLanguageExplanation().then(r => {
            this.expressionLanguageExplanation = r.data
        })
        i18nApi.getLanguages().then(r => {
            this.languages = r.data
            this.ensureExplanationRuleTranslations()
        })
    },
    methods: {
        onChange({ description, expression, rule }) {
            if (typeof description !== 'undefined') {
                this.$set(rule, 'description', description)
            }

            this.$set(rule, 'expression', expression)
        },
        add(type) {
            const rule = {
                type,
                description: '',
                expression: '',
            }

            if (this.isExplanationRuleType(type)) {
                rule.translations = this.createEmptyTranslations()
            }

            this.legislation.expressions.push(rule)
        },
        remove(rule) {
            this.$swal({
                title: 'Delete business rule?',
                text: 'The rule will be removed from this form. It is permanently deleted only after you click Save. You can discard this change by reloading the page before saving.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Delete',
                cancelButtonText: this.$t('Component(Button):cancel'),
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-outline-secondary ml-1',
                },
                buttonsStyling: false,
            }).then(result => {
                if (result.isConfirmed) {
                    const index = this.legislation.expressions.indexOf(rule)
                    if (index >= 0) {
                        this.legislation.expressions.splice(index, 1)
                    }
                }
            })
        },
        showExplanation() {
            this.$refs.explanationModal.show()
        },
        groupTitle(group) {
            return group.titleKey ? this.$t(group.titleKey) : group.title
        },
        expressionsByType(type) {
            return this.legislation.expressions.filter(e => e.type === type)
        },
        isExplanationRuleType(type) {
            return explanationRuleTypes.includes(type)
        },
        createEmptyTranslations() {
            const translations = {}

            this.languages.forEach(language => {
                translations[language.code] = {
                    title: '',
                    description: '',
                }
            })

            return translations
        },
        ensureExplanationRuleTranslations() {
            this.legislation.expressions
                .filter(rule => this.isExplanationRuleType(rule.type))
                .forEach(rule => {
                    if (!rule.translations) {
                        this.$set(rule, 'translations', {})
                    }

                    this.languages.forEach(language => {
                        if (!rule.translations[language.code]) {
                            this.$set(rule.translations, language.code, {
                                title: '',
                                description: '',
                            })
                        }
                    })
                })
        },
    },
}
</script>

<template>
    <div>
        <ExplanationModal
            ref="explanationModal"
            :explanation="expressionLanguageExplanation"
        />

        <b-card no-body>
            <div class="card-header">
                <div class="col-md-10 pl-0">
                    <b-card-title>
                        {{ $t('Component(Text):business-rules') }}
                    </b-card-title>
                </div>
                <div class="col-md-2 pr-0 mr-0 text-right">
                    <Button @click="showExplanation">
                        Help
                    </Button>
                </div>
            </div>
            <div class="pl-2 pr-2 pb-1">
                <b-tabs>
                    <b-tab
                        v-for="group in businessRuleGroups"
                        :key="group.type"
                    >
                        <template #title>
                            <span>{{ groupTitle(group) }}</span>
                            <b-badge
                                class="ml-50"
                                variant="light-secondary"
                            >
                                {{ expressionsByType(group.type).length }}
                            </b-badge>
                        </template>

                        <div class="business-rule-tab pt-2">
                            <div class="d-flex align-items-center justify-content-between mb-1">
                                <h5 class="mb-0">
                                    {{ groupTitle(group) }}
                                </h5>
                                <Button
                                    class="btn-icon"
                                    @click="add(group.type)"
                                >
                                    <feather-icon icon="PlusIcon" />
                                </Button>
                            </div>

                            <div
                                v-if="expressionsByType(group.type).length === 0"
                                class="text-muted py-2"
                            >
                                No business rules configured for this type.
                            </div>

                            <div
                                v-for="(rule, index) in expressionsByType(group.type)"
                                :key="rule.id || group.type + '-' + index"
                                class="business-rule-item"
                            >
                                <BusinessRule
                                    :rule="rule"
                                    :show-description="true"
                                    :explanation="expressionLanguageExplanation"
                                    @change="onChange"
                                    @remove="remove"
                                />
                            </div>
                        </div>
                    </b-tab>
                </b-tabs>
            </div>
        </b-card>

        <b-card no-body>
            <div class="card-header">
                <div class="col-md-12 pl-0">
                    <b-card-title>
                        Explanation rules
                    </b-card-title>
                </div>
            </div>
            <div class="pl-2 pr-2 pb-1">
                <b-tabs>
                    <b-tab
                        v-for="group in explanationRuleGroups"
                        :key="group.type"
                    >
                        <template #title>
                            <span>{{ groupTitle(group) }}</span>
                            <b-badge
                                class="ml-50"
                                variant="light-secondary"
                            >
                                {{ expressionsByType(group.type).length }}
                            </b-badge>
                        </template>

                        <div class="business-rule-tab pt-2">
                            <div class="d-flex align-items-center justify-content-between mb-1">
                                <h5 class="mb-0">
                                    {{ groupTitle(group) }}
                                </h5>
                                <Button
                                    class="btn-icon"
                                    @click="add(group.type)"
                                >
                                    <feather-icon icon="PlusIcon" />
                                </Button>
                            </div>

                            <div
                                v-if="expressionsByType(group.type).length === 0"
                                class="text-muted py-2"
                            >
                                No explanation rules configured for this type.
                            </div>

                            <div
                                v-for="(rule, index) in expressionsByType(group.type)"
                                :key="rule.id || group.type + '-' + index"
                                class="business-rule-item"
                            >
                                <BusinessRuleExplanation
                                    :rule="rule"
                                    :languages="languages"
                                    :explanation="expressionLanguageExplanation"
                                    @change="onChange"
                                    @remove="remove"
                                />
                            </div>
                        </div>
                    </b-tab>
                </b-tabs>
            </div>
        </b-card>
    </div>
</template>

<style scoped lang="scss">
.business-rule-item {
    border-top: 1px solid #ebe9f1;
    padding-top: 1rem;

    &:not(:last-child) {
        margin-bottom: 1rem;
        padding-bottom: 1rem;
    }
}
</style>
